import React from "react";
import { Box, Button, TableCell, TableRow, Tooltip, Checkbox } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import moment from "moment";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Restore as RestoreIcon, Visibility as VisibilityIcon, Edit as EditIcon, Delete as DeleteIcon, } from "@mui/icons-material";
import { trackActivity } from "../../../../utils/mixpanel";
import UploadingIcon from "../../../../shared_elements/components/UploadingIcon";
import AuditPopover from "../../../../shared_elements/components/popover";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PermissionWrapper } from "../../../../wrappers";
import { useSelector } from "react-redux";
import { dateMonthYearDateFormat } from "../../../../constants";

const statusMap = {
  draft: {
    label: "Draft",
    color: "#89bfff",
    backgroundImage: "linear-gradient(90deg,#bedbfd,#dcebfc 95%)",
  },
  active: {
    label: "Active",
    color: "#5ec264",
    backgroundImage: "linear-gradient(90deg,#beed87,#d2e9b6 95%)",
  },
  closed: {
    label: "Closed",
    color: "#d32f2f",
    backgroundImage: "linear-gradient(269deg,#fcd5d5,#fca8a8 95%)",
  },
  terminated: {
    label: "Terminated",
    color: "#ff9800",
    backgroundImage: "linear-gradient(269deg,#fadfb8,#fdd79e 95%)",
  },
  "on-hold": {
    label: "On-hold",
    color: "#ee7c26",
    backgroundImage: "linear-gradient(269deg,#ffdfcb,#ffcdad 95%)",
  },
};

const LeaseLabel = ({ label, statusChange, isStatusChangeAllowed = true }) => {
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Asset Lease Listing`, page_type: page_type, })
  }
  const statusKey = label?.toLowerCase?.();
  const status = statusMap[statusKey] || { label: label || "-", color: "#000" };
  const handleButtonClick = () => {
    if (!isStatusChangeAllowed) return;
    statusChange();
    handleItemClicked('Lease Status Change Button')
  };
  return (
    <Button
      variant="contained"
      onClick={handleButtonClick}
      disabled={!isStatusChangeAllowed}
      endIcon={isStatusChangeAllowed ? <ArrowDropDownIcon /> : null}
      sx={{
        backgroundImage: status.backgroundImage,
        color: status.color,
        textTransform: "capitalize",
        padding: "0 6px",
        fontWeight: "600",
        alignItems: "center",
        fontSize: "13px",
        whiteSpace: "nowrap",
        width: "110px",
        cursor: isStatusChangeAllowed ? "pointer" : "not-allowed",
        justifyContent: "center",
        "& .MuiButton-endIcon": {
          marginLeft: "0px",
        },
        "&.Mui-disabled": {
          cursor: "not-allowed",
          opacity: 1,
        },
      }}
    >
      {status.label}
    </Button>

  );
};

const LeaseInfoList = ({ item, bulk, onEditFn, onView, viewRevisonHistory, onDeleteEolFn, toggleBulkOps, statusChange, onOpenAttachments, uploadFileProgressData, uploadedFileData, viewFileStatus, slug }) => {
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Asset Lease Listing`, page_type: page_type, })
  }

  const { permission } = useSelector((state) => state.leases);
  const leasePermission = permission?.permissions?.lease_revamp_application?.lease_revamp?.lease_details;
  const leaseStatus = Array.isArray(leasePermission) ? leasePermission.includes('U') : false;

  return (
    <TableRow>
      <TableCell className="table-actions-list">
        <Box className="table-icon-container">
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease", operationType: ['D'] }}>
            <Checkbox style={{ verticalAlign: 'super' }} color="primary" size='small' disabled={item?.status !== "Draft"} checked={bulk?.ids.find(items => items?.id === item?.id) ? true : false} onChange={toggleBulkOps} />
          </PermissionWrapper>
          <AuditPopover item={item}>
            <MoreVertIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }} />
          </AuditPopover>
          <Tooltip title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => { handleItemClicked('RevisionHistoryIcon'); viewRevisonHistory(); }} fontSize='small' color='primary' /></Tooltip>
          <Tooltip title="View"><VisibilityIcon sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('ViewIcon') }} fontSize='small' color='primary' /></Tooltip>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease", operationType: ['U'] }}>
            <Tooltip title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { onEditFn(); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip></PermissionWrapper>
          {
            item.status === "Draft" && <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease", operationType: ['D'] }}><Tooltip title="Delete"><DeleteIcon sx={{ cursor: 'pointer' }} onClick={() => { onDeleteEolFn(); handleItemClicked('DeleteIcon') }} fontSize='small' color='error' /></Tooltip></PermissionWrapper>
          }
          {
            item?.attachments?.length > 0 && (
              <Tooltip title="Attached File(s)">
                <Box className="attachment-container">
                  <AttachFileIcon fontSize="small" color="primary" onClick={() => { onOpenAttachments(); handleItemClicked('AttachmentsIcon') }} sx={{ cursor: "pointer" }} />
                  <Box className="attachment-count"> ({item.attachments.length}) </Box>
                </Box>
              </Tooltip>
            )
          }
          {(uploadedFileData || slug === item?.slug || uploadFileProgressData?.response?.data?.data?.failed_files[0]?.record_slug === item?.slug) && <UploadingIcon viewFileStatus={() => viewFileStatus(uploadedFileData)} />}
        </Box>
      </TableCell>
      <TableCell > <LeaseLabel label={item?.status} statusChange={() => statusChange(item)} isStatusChangeAllowed={leaseStatus} /></TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}> {item?.reference_code || "-"}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}> {item?.assigned_code || "-"}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}> {item?.lease_type?.label || "-"}</TableCell>

      <TableCell sx={{ whiteSpace: "normal", cursor: 'pointer', wordBreak: 'break-word' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>
        {item?.lessee?.logo ? (
          <>
            <Tooltip title={item?.lessee?.name ? item?.lessee?.name :  null} placement="top" arrow>
              <Box className="lessee-logo-card">
                <img src={item.lessee.logo} alt={item?.lessee?.name || "Lessee"} loading="lazy" className="lessee-logo-img" />
              </Box>
            </Tooltip>
          </>
        ) : (item?.lessee?.name || "--")}
      </TableCell>
      
      <TableCell sx={{ whiteSpace: "normal", cursor: 'pointer', wordBreak: 'break-word', textAlign: 'center' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }} >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
          <span> {item?.start_date && item?.end_date ? `${moment(item.start_date).format(dateMonthYearDateFormat).toUpperCase()} - ${moment(item.end_date).format(dateMonthYearDateFormat).toUpperCase()}` : ""} </span>
          <span style={{fontSize: "12px"}}> {item?.lease_term_phrase || "--"} </span>
        </Box>
      </TableCell>
      <TableCell align='center' sx={{ cursor: 'pointer', minWidth: '80px' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>{item?.advanced_rental_invoice_is_due_day_last_day_month ? 'End of Month' : (item?.advanced_rental_invoice_generation_day || item?.advanced_rental_invoice_due_day) ? Number(item?.advanced_rental_invoice_generation_day || 0) + Number(item?.advanced_rental_invoice_due_day || 0) : "--"}</TableCell>
      <TableCell align='center' sx={{ cursor: 'pointer', minWidth: '80px' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>{item?.reserve_invoice_is_due_day_last_day_month ? 'End of Month' : (item?.invoice_generation_day || item?.invoice_due_day) ? Number(item?.invoice_generation_day || 0) + Number(item?.invoice_due_day || 0) : "--"}</TableCell>
    </TableRow>
  );
};

export default LeaseInfoList;
